import { useEffect } from 'react';
import { createGlobalStyle } from 'styled-components';
import { rgba } from 'polished';

export const GlobalStyles = createGlobalStyle`
  /* Header icon wiggle animation for unread badges */
  @keyframes header-icon-wiggle {
    0% { transform: rotate(0deg); }
    2% { transform: rotate(-12deg); }
    4% { transform: rotate(10deg); }
    6% { transform: rotate(-8deg); }
    8% { transform: rotate(6deg); }
    10% { transform: rotate(-2deg); }
    12%, 100% { transform: rotate(0deg); }
  }

  .header-icon-has-unread {
    animation: header-icon-wiggle 5s ease-in-out infinite;

    @media (prefers-reduced-motion: reduce) {
      animation: none;
    }
  }

  /* Scrollbar: Firefox fallback (CSS-only, no glow) */
  * {
    scrollbar-width: thin;
    scrollbar-color: #00795b #ffffff;
  }

  /* WebKit scrollbar CSS injected via useScrollbarGlow() hook in <head> */

  html,
  body,
  #root {
    font-family: ${({ theme }) => theme.font.family};
    color: ${({ theme }) => theme.palette.text.primary};
    letter-spacing: 0.5px;
  }

  canvas {
    display: block;
    height: 100%;
  }

  body {
    line-height: 1.35 !important;
    text-rendering: optimizeLegibility;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    font-family: ${({ theme }) => theme.font.family};
    font-weight: ${({ theme }) => theme.font.weight.regular};
    color: ${({ theme }) => theme.palette.text.primary};
    letter-spacing: 0.5px;

    @media screen and (min-width: ${({ theme }) =>
      theme.breakpoints.sm + 320}px ) {
      font-size: ${({ theme }) => theme.font.size.lg};
    }
  }

  [class^="ant-"], [class*=" ant-"] {
    font-family: ${({ theme }) => theme.font.family};
    letter-spacing: 0.5px;
  }

  /* Remove focus outline/border from main content wrappers */
  .simplebar-content-wrapper,
  .ant-layout-content,
  .ant-layout-content:focus,
  .ant-layout-content:focus-visible {
    outline: none !important;
    border: none !important;
  }

  a,
  .link {
    cursor: pointer;
    color: ${({ theme }) => theme.palette.text.secondary};

    .anticon {
      color: inherit;
    }
  }

  // ul,
  // ol,
  // p {
  //   margin-bottom: 0 !important;
  // }

  ul,
  ol {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  img {
    max-width: 100%;
    height: auto;
  }

  h1, h2, h3, h4, h5, h6 {
    color: ${({ theme }) => theme.palette.text.primary};
    font-weight: 500;
    font-family: ${({ theme }) => theme.font.family};
  }

  h1 {
    font-size: 32px;
    font-weight: 600;
  }

  h2 {
    font-size: 26px;
    font-weight: 600;
  }

  h3 {
    font-size: 22px;
    font-weight: 600;
  }

  h4 {
    font-size: 18px;
    font-weight: 600;
  }

  h5 {
    font-size: 16px;
    font-weight: 500;
  }

  h6 {
    font-size: 14px;
    font-weight: 500;
  }

  .pointer {
    cursor: pointer;
  }

  .text-truncate {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .card-hover {
    transition: all 0.3s ease;
    transform: scale(1);

    &:hover {
      box-shadow: 0 4px 8px rgba(0, 0, 0, .45);
      transform: scale(1.05);
    }
  }

  .text-uppercase {
    text-transform: uppercase;
  }

  .ant-tabs .ant-tabs-tab {
    padding: 8px 16px;
  }
  .ant-tabs-tab .ant-tabs-tab-btn {
    font-size: 16px;
    font-weight: 400;
    text-shadow: none !important;
    color: ${({ theme }) => theme.palette.text.sidebar};
  }
  .ant-tabs-tab.ant-tabs-tab-active .ant-tabs-tab-btn {
    font-weight: 500;
    color: ${({ theme }) => theme.palette.secondary.main} !important;
  }

  .ant-tabs-ink-bar {
    background: ${({ theme }) => theme.palette.secondary.main} !important;
  }

  .ant-btn {
    height: 40px;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 500;

    outline: none !important;
    box-shadow: none !important;
    outline-offset: 0 !important;
    
    &:hover,
    &:focus,
    &:active,
    &:focus-visible {
      outline: none !important;
      box-shadow: none !important;
      outline-offset: 0 !important;
    };
  }

  .ant-btn-color-default,
  .ant-btn-variant-solid,
  .ant-btn-variant-outlined {
    outline: none !important;
    box-shadow: none !important;
    outline-offset: 0 !important;
    &:hover,
    &:focus,
    &:active,
    &:focus-visible {
      outline: none !important;
      box-shadow: none !important;
      outline-offset: 0 !important;
    };
  }

  .ant-btn-primary {
    background: ${({ theme }) => theme.palette.secondary.main};
    border-color: ${({ theme }) => theme.palette.secondary.main};
    color: ${({ theme }) => theme.palette.white};
    box-shadow: none;

    &:hover,
    &:focus {
      background: ${({ theme }) => theme.palette.secondary.main};
      border-color: ${({ theme }) => theme.palette.secondary.main};
      color: ${({ theme }) => theme.palette.white};
      box-shadow: none;
    }
  }

  .btn-secondary {
    background-color: ${({ theme }) => theme.palette.secondary.main};
    border-color: ${({ theme }) => theme.palette.secondary.main};
    color: ${({ theme }) => theme.palette.white};

    &:hover, &:focus {
      background: ${({ theme }) => theme.palette.secondary.main};
      border-color: ${({ theme }) => theme.palette.secondary.main};
      color: ${({ theme }) => theme.palette.white};
      box-shadow: none;
    }
  }

  .ant-spin .ant-spin-dot-holder {
    color: ${({ theme }) => theme.palette.text.secondary};
  }

  .ant-list-item {
    justify-content: flex-start !important;
    color: ${({ theme }) => theme.palette.text.secondary};
  }

  .ant-spin-dot i {
    background: ${({ theme }) => theme.palette.secondary.main} !important;
  }

  .ant-spin-dot-item {
    background-color: ${({ theme }) => theme.palette.secondary.main} !important;
  }

  .ant-table {
    border: none;
    border-radius: 0;
    background: transparent !important;
    color: ${({ theme }) => theme.palette.text.primary} !important;
  }

  .ant-table-wrapper *:focus,
  .ant-table-wrapper *:focus-visible {
    outline: none !important;
  }

  .text-lowercase {
    text-transform: lowercase;
  }

  .text-capitalize {
    text-transform: capitalize;
  }

  .text-left {
    text-align: left;

    [dir=rtl] & {
      text-align: right;
    }
  }

  .text-center {
    text-align: center;
  }

  .text-right {
    text-align: right;

    [dir=rtl] & {
      text-align: left;
    }
  }

  .background-image {
    background-repeat: no-repeat;
    background-position: center center;
    background-size: cover;
  }

  .cr-btn {
    box-sizing: border-box;
    box-shadow: 0 3px 1px -2px rgba(0, 0, 0, 0.2), 0px 2px 2px 0px rgba(0, 0, 0, 0.14), 0px 1px 5px 0px rgba(0, 0, 0, 0.12);
    text-transform: uppercase;
  }

  .cr-btn-circle {
    background-color: transparent;
    border-color: transparent;
    color: ${({ theme }) => theme.palette.gray[500]};
    box-shadow: none;

    &:hover, &:focus {
      background-color: ${({ theme }) => theme.palette.tooltipBg + '04'};
      border-color: ${({ theme }) => theme.palette.tooltipBg + '04'};
      color: ${({ theme }) => theme.palette.text.primary};
    }

    & .anticon {
      font-size: 20px;
    }
  }

  .mb-0 {
    margin-bottom: 0 !important;
  }

  .dot {
    height: 12px;
    width: 12px;
    display: block;
    border-radius: ${({ theme }) => theme.sizes.borderRadius.circle};
  }

  .close-btn {
    background-color: transparent;
    color: ${({ theme }) => theme.palette.text.primary};
    box-shadow: none;
    border: 0 none;
    padding: 0;
    height: auto;

    &:hover, &:focus {
      border: 0 none;
    }

    & .anticon {
      font-size: 20px;
    }
  }

  .page-title {
    color: ${({ theme }) => theme.palette.text.primary};
    font-weight: ${({ theme }) => theme.font.weight.bold};
    margin-bottom: 16px;
    font-size: ${({ theme }) => theme.font.size.lg};
    display: inline-block;
  }

  .ant-checkbox-inner {
    width: 18px;
    height: 18px;
  }

  .ant-checkbox-indeterminate .ant-checkbox-inner::after {
    width: 12px;
    height: 12px;
  }

  .ant-modal {
    max-width: 90%;
  }

  .text-secondary {
    color: ${({ theme }) => theme.palette.text.secondary};
  }

  .text-green {
    //color: @green-6;
  }

  .z-index-20 {
    z-index: 20;
  }

  .ant-picker {
    //padding-top: @input-padding-vertical-base;
    //padding-bottom: @input-padding-vertical-base;
    .ant-picker-input >input {
      font-size: 16px;
      font-weight: 400;
      border-radius: 8px;
      color: rgba(22, 23, 26, 1);
    }
  }

  .ant-drawer,
  .ant-modal-wrap,
  .ant-modal-mask,
  .ant-picker-dropdown,
  .ant-select-dropdown,
  .ant-message,
  .ant-notification {
    z-index: 1051;
  }

  /* NX-5529: TopBarStack (env / classic-UI / urgent banners — NX-5160) is
     sticky at the top with z-index 1100, above toasts (1051). Offset the fixed
     toast below that strip so it is not obscured by it. --nxsys-topbar-h is
     published by TopBarStack (0px when empty, e.g. prod → default 24px top). */
  .ant-notification-topLeft,
  .ant-notification-top,
  .ant-notification-topRight {
    top: calc(var(--nxsys-topbar-h, 0px) + 24px) !important;
  }

  .ant-message {
    font-size: 16px;
    font-weight: 500;
    line-height: 24px;
    color: rgba(0, 0, 0, 1);
    /* NX-5529: keep messages below the sticky TopBarStack banners too */
    top: calc(var(--nxsys-topbar-h, 0px) + 8px) !important;
    .ant-message-notice-wrapper {
      padding: 8px 20px;
      text-align: right;
      .ant-message-custom-content {
        display: inline-flex;
        align-items: flex-start;
        text-align: left;
      }
      .ant-message-custom-content>.anticon {
        font-size: 20px;
        margin-inline-end: 12px;
        margin-top: 2px;
      }
      .ant-message-notice-wrapper .ant-message-success>.anticon {
        color: rgba(22, 179, 100, 1);
      }
      .ant-message-notice-content {
        padding: 16px 24px;
        border: 1px solid rgba(213, 217, 222, 1);
        box-shadow: 0 1px 2px 0 rgba(16, 24, 40, 0.05);
        /* Cap width so long toasts wrap onto multiple lines instead of
           stretching across the whole viewport; stay responsive on narrow screens.
           The wrapper is right-aligned, so the capped pill sits top-right. */
        max-width: min(560px, calc(100vw - 32px));
        overflow-wrap: anywhere;
        margin-inline-end: 16px;
      }
    }
  }

  .ant-notification {
    .ant-notification-notice-wrapper {
      border: 1px solid rgba(213, 217, 222, 1);
      box-shadow: 0 1px 2px 0 rgba(16, 24, 40, 0.05);
      .ant-notification-notice {
        width: 390px;
        max-width: calc(100vw - 48px);
        padding: 16px 23px;
      }
      .ant-notification-notice-with-icon .ant-notification-notice-message {
        font-size: 16px;
        font-weight: 500;
        line-height: 24px;
        color: rgba(0, 0, 0, 1);
        margin-bottom: 0px;
        margin-inline-start: 32px;
      }
      .ant-notification-notice-icon {
        font-size: 20px;
      }
    }
  }

  input:hover,
  input:focus,
  input:focus-within
  input:active {
    box-shadow: none !important;
    outline: none !important;
    border-color: ${({ theme }) => theme.palette.secondary.main} !important;
  }

  .ant-input {
    &.ant-input-outlined {
      border-color: ${({ theme }) => theme.palette.neutral[300]};
    }
    &.ant-input-outlined:hover,
    &.ant-input-outlined:focus,
    &.ant-input-outlined:focus-within {
      box-shadow: none !important;
      outline: none !important;
      border-color: ${({ theme }) => theme.palette.secondary.main} !important;
    }
  }

  .ant-input-affix-wrapper.ant-input-affix-wrapper-outlined,
  .ant-picker.ant-picker-outlined,
  .ant-input-number.ant-input-number-outlined {
    border-color: ${({ theme }) => theme.palette.neutral[300]};
    &:hover,
    &:focus,
    &:focus-within {
      box-shadow: none !important;
      border-color: ${({ theme }) => theme.palette.secondary.main} !important;
    }
  }

  .ant-select {
    &.ant-select-outlined:not(.ant-select-customize-input) .ant-select-selector {
      border: 1px solid ${({ theme }) => theme.palette.neutral[300]};
    }
      
    &:hover,
    &:focus,
    &:active,
    &.ant-select-focused {
      .ant-select-selector {
        box-shadow: none !important;
        border-color: ${({ theme }) => theme.palette.secondary.main} !important;
      }
    }

    &.ant-select-single:not(.ant-select-customize-input)
      .ant-select-selector
      .ant-select-selection-search-input {
      color: ${({ theme }) => theme.palette.text.primary};
    }

    &.ant-select-single:not(.ant-select-disabled) .ant-select-selector .ant-select-selection-item {
      font-weight: 400;
      color: rgba(22, 23, 26, 1);
    }

    .ant-select-single:not(.ant-select-disabled) .ant-select-selector .ant-select-selection-placeholder {
      font-weight: 400;
      color: ${({ theme }) => theme.palette.text.disabled};
    }
  }

  /* Disabled & readonly form inputs: readable text, consistent appearance */
  .ant-input-disabled,
  .ant-input[disabled],
  .ant-input[readonly],
  .ant-input-affix-wrapper-disabled,
  .ant-input-affix-wrapper-disabled .ant-input,
  .ant-input-affix-wrapper-readonly .ant-input,
  .ant-picker-disabled .ant-picker-input > input,
  .ant-select-disabled .ant-select-selection-item,
  .ant-input-number-disabled .ant-input-number-input,
  .ant-input-number-readonly .ant-input-number-input,
  .ant-checkbox-disabled + span,
  .ant-radio-disabled + span,
  .ant-cascader-picker-disabled .ant-cascader-picker-label {
    color: ${({ theme }) => theme.palette.text.primary} !important;
    -webkit-text-fill-color: ${({ theme }) => theme.palette.text.primary} !important;
  }

  .ant-input-disabled,
  .ant-input[disabled],
  .ant-input[readonly],
  .ant-input-affix-wrapper-disabled,
  .ant-input-affix-wrapper-readonly,
  .ant-picker-disabled,
  .ant-select-disabled,
  .ant-input-number-disabled,
  .ant-input-number-readonly {
    cursor: not-allowed !important;

    * {
      cursor: not-allowed !important;
    }
  }

  /* Disabled & readonly: hide placeholders — blank = no data */
  .ant-input-disabled::placeholder,
  .ant-input[disabled]::placeholder,
  .ant-input[readonly]::placeholder,
  .ant-input-affix-wrapper-disabled .ant-input::placeholder,
  .ant-input-affix-wrapper-readonly .ant-input::placeholder,
  .ant-picker-disabled .ant-picker-input > input::placeholder,
  .ant-input-number-disabled .ant-input-number-input::placeholder,
  .ant-input-number-readonly .ant-input-number-input::placeholder,
  .ant-select-disabled .ant-select-selection-placeholder {
    color: transparent !important;
    -webkit-text-fill-color: transparent !important;
    opacity: 1 !important;
  }

  .ant-select-dropdown {
    box-shadow: none;
    border: 1px solid rgba(234, 236, 239, 1);
    .ant-select-item-option {
      color: inherit;
      font-weight: 400;
    }
    .ant-select-item-option-selected:not(.ant-select-item-option-disabled),
    .ant-select-item-option-active:not(.ant-select-item-option-disabled) {
      color: inherit;
      font-weight: 400;
      background-color: #F3FFFC;
    }
    .ant-select-item-option-disabled {
      color: ${({ theme }) => theme.palette.neutral[300]};
      font-weight: 400;
    }
  }

  .ant-drawer-close {
    color: ${({ theme }) => theme.palette.text.secondary};
    width: 36px;
    height: 36px;
    border-radius: 50%;
    top: 11px;
    right: 6px;
    padding: 5px;

    &:hover,
    &:focus {
      background-color: ${({ theme }) => theme.palette.background.default};
      color: ${({ theme }) => theme.palette.text.primary};

    }
  }

  // Ant Tooltip
  .ant-tooltip-inner {
    & a {
      color: ${({ theme }) => theme.palette.white};
      display: flex;
      align-items: center;

      & .ant-menu-item-icon {
        margin-right: 6px;

        & .anticon, & svg {
          display: block;
        }
      }
    }
  }


  /* Collapsed sidebar submenu popup: match expanded sidebar style */
  .ant-menu-submenu-popup .ant-menu.ant-menu-vertical {
    padding: 4px !important;

    & > .ant-menu-item {
      border-radius: 6px !important;
      margin: 2px 0 !important;
      padding-left: 16px !important;
      padding-right: 16px !important;
      height: 36px !important;
      line-height: 36px !important;
      width: 100% !important;
      transition: background-color 0.2s ease !important;

      & *,
      & a,
      & .ant-menu-title-content {
        transition: none !important;
      }

      &:hover:not(.ant-menu-item-selected),
      &.ant-menu-item-active:not(.ant-menu-item-selected) {
        background: linear-gradient(
          278.36deg,
          #00ca98 47.11%,
          #079a76 97.82%
        ) !important;
        color: #fff !important;

        & .ant-menu-item-icon img,
        & .anticon img {
          filter: brightness(0) invert(1) !important;
        }

        & .ant-menu-item-icon,
        & .anticon {
          color: #fff !important;
        }
      }

      &.ant-menu-item-selected {
        background: linear-gradient(
          278.36deg,
          #00ca98 47.11%,
          #079a76 97.82%
        ) !important;
        color: #fff !important;
        font-weight: 600;

        & .ant-menu-item-icon img,
        & .anticon img {
          filter: brightness(0) invert(1) !important;
        }

        & .ant-menu-item-icon,
        & .anticon {
          color: #fff !important;
        }
      }
    }
  }

  .item-hover {
    transition: all .2s ease;
    transform: scale(1);

    &:hover {
      background-color: ${({ theme }) => rgba(theme.palette.primary.main, 0.1)};
      transform: translateY(-2px);
      box-shadow: 0 3px 10px 0 ${({ theme }) =>
        rgba(theme.palette.primary.main, 0.1)};
    }
  }


  .cr-dropdown-link {
    width: 30px;
    height: 30px;
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: ${({ theme }) => theme.sizes.borderRadius.circle};
    color: ${({ theme }) => rgba(theme.palette.tooltipBg, 0.84)};
    font-size: 20px;

    &:hover {
      background-color: ${({ theme }) => rgba(theme.palette.tooltipBg, 0.1)};
    }
  }

  .slick-slider-global .slick-dots li {
    width: 12px;
    height: 12px;
    margin: 0 2px;
    vertical-align: middle;
  }

  .slick-slider-global .slick-dots li button {
    width: 12px;
    height: 12px;
    padding: 0;
  }

  .slick-slider-global .slick-dots li.slick-active button:before {
    color: #825a44;
    opacity: .75;
  }

  .slick-slider-global .slick-dots li button:before {
    width: 12px;
    height: 12px;
    font-size: 10px;
    line-height: 12px;
    color: #be8658;
  }

  .ant-card {
    border-radius: 8px;
    line-height: 1.35;
  }


  .ant-layout .ant-layout-header {
    padding-inline: 30px;

    @media screen and (max-width: ${({ theme }) => theme.breakpoints.md}px) {
      padding-inline: 20px;
    }
  }

  .ant-btn, .ant-btn > .anticon {
    line-height: 1;
  }

  .ant-select-outlined:hover,
  .ant-select-outlined:focus {
    .ant-select-selector {
      border-color: ${({ theme }) => theme.palette.secondary.main} !important;
    }
  }

  .ant-input::placeholder {
    color: ${({ theme }) => theme.palette.text.disabled} !important;
  }

  .ant-dropdown-trigger {
    background: transparent;
    color: ${({ theme }) => theme.palette.text.primary} !important;
  }

  .ant-progress-text {
    color: ${({ theme }) => theme.palette.text.primary} !important;
  }

  .anticon {
    background: transparent;
  }

  .ant-modal-content {
    background: ${({ theme }) => theme.palette.background.paper} !important;

    & .ant-modal-header {
      background: ${({ theme }) => theme.palette.background.paper} !important;

      & .ant-modal-title {
        color: ${({ theme }) => theme.palette.text.primary} !important;
      }
    }

    & .ant-modal-close-icon {
      color: ${({ theme }) => theme.palette.text.primary} !important;
    }
  }

  .ant-card {
    background: ${({ theme }) => theme.palette.background.paper};
    color: ${({ theme }) => theme.palette.text.primary};
    border-radius: ${({ theme }) => theme.cardRadius};
  }

  .ant-radio-wrapper,
  .ant-list-item-meta-title,
  .ant-tabs-tab .ant-tabs-tab-btn,
  .ant-picker-content th,
  .ant-table-thead .ant-table-cell {
    color: ${({ theme }) => theme.palette.text.sidebar};
  }

  .ant-collapse-content > .ant-collapse-content-box {
    color: ${({ theme }) => theme.palette.text.secondary};
    background: ${({ theme }) => theme.palette.background.paper};
    border-radius: 0 0 8px 8px;
  }

  .ant-select-arrow,
  .ant-picker-cell,
  .ant-list-item-meta-description {
    color: ${({ theme }) => theme.palette.text.secondary};
  }

  .ant-checkbox-input {
    background-color: ${({ theme }) => theme.palette.background.paper};
  }

  .rnc__notification-container--top-right {
    right: 320px !important;
  }

  .rnc__notification-container--bottom-center,
  .rnc__notification-container--bottom-left {
    bottom: 200px !important;
  }

  .rnc__notification-container--bottom-right {
    bottom: 200px !important;
    right: 320px !important;
  }

  .boxedLayout {
    @media screen and (min-width: ${({ theme }) =>
      theme.breakpoints.xl + 80}px) {
      max-width: 1260px;
      margin-left: auto;
      margin-right: auto;

      #root {
        box-shadow: 0px 0px 4px 2px rgba(0, 0, 0, 0.12);
      }

      & .appMainFixedFooter {
        & .app-main-footer {
          max-width: 1260px;
          margin-left: auto;
          margin-right: auto;
        }
      }

      & .mini-sidebar-toggle.ant-layout-sider,
      & .mini-sidebar.ant-layout-sider,
      & .userMiniHeader-sidebar.ant-layout-sider {
        position: absolute;
      }

      & .app-header-mini-sidebar,
      & .app-header-mini,
      & .app-userMiniHeader {
        position: absolute;
        width: 100% !important;
      }

      & .mini-sidebar-toggle.ant-layout-sider-collapsed + .app-layout-mini-sidebar-main .app-header-mini-sidebar,
      & .mini-sidebar.ant-layout-sider-collapsed + .app-layout-mini-main .app-header-mini,
      & .userMiniHeader-sidebar.ant-layout-sider-collapsed + .app-layout-userMiniHeader-main .app-userMiniHeader {
        width: 100% !important;
      }

      & .app-layout-mini-sidebar,
      & .app-layout-mini,
      & .app-layout-userMiniHeader {
        padding-top: 0;
      }

      & .app-layout-mini-sidebar-main,
      & .app-layout-mini-main,
      & .app-layout-userMiniHeader-main {
        padding-top: 71px;
      }

      & .coin-stats-content h3 {
        font-size: ${({ theme }) => theme.font.size.lg};
      }

      & .coin-stats-content span {
        font-size: ${({ theme }) => theme.font.size.sm};
      }
    }

    @media screen and (min-width: ${({ theme }) => theme.breakpoints.xxl}px) {
      max-width: 1460px;

      & .appMainFixedFooter {
        & .app-main-footer {
          max-width: 1460px;
        }
      }
    }
  }

  //Framed Layout
  .framedLayout {
    @media screen and (min-width: ${({ theme }) =>
      theme.breakpoints.xl + 80}px) {
      padding: ${({ theme }) => theme.sizes.framed.base};
      background-color: ${({ theme }) => theme.palette.secondary.main};

      #root {
        height: calc(100vh - 2 * ${({ theme }) => theme.sizes.framed.base});
        overflow: hidden;
        box-shadow: 0px 0px 4px 2px rgba(0, 0, 0, 0.12);
        border-radius: 12px;
      }

      &.framedHorLayout,
      &.framedHorDarkLayout,
      &.framedHorHeaderFixedLayout {

        #root {
          overflow-y: auto;
        }
      }

      & .app-sidebar-scrollbar,
      & .main-scrollbar,
      & .app-mini-sidebar-scrollbar,
      & .main-mini-sidebar-scrollbar,
      & .main-mini-scrollbar,
      & .standard-main-scrollbar,
      & .userHeader-main-scrollbar,
      & .main-userMiniHeader-scrollbar,
      & .drawerLayout-main-scrollbar,
      & .app-BitBucket-sidebar-scrollbar {
        max-height: calc(100vh - (71px + 2 * ${({ theme }) =>
          theme.sizes.framed.base}));
      }

      & .app-standard-sidebar-scrollbar {
        max-height: calc(100vh - (140px + 2 * ${({ theme }) =>
          theme.sizes.framed.base}));
      }

      & .app-userHeader-sidebar-scrollbar,
      & .app-sidebar-userMiniHeader-scrollbar,
      & .bitBucket-main-scrollbar,
      & .bucket-minibar {
        max-height: calc(100vh - (2 * ${({ theme }) =>
          theme.sizes.framed.base}));
      }

      & .app-layout {
        height: calc(100vh - 2 * ${({ theme }) => theme.sizes.framed.base});
        min-height: 10px;
      }

      & .auth {
        min-height: 10px;
        height: 100%;
      }

      & .main-auth-scrollbar {
        min-height: 10px;
      }

      & .apps-container {
        height: calc(100vh - (153px + 2 * ${({ theme }) =>
          theme.sizes.framed.base}));
      }

      & .app-layout-mini-sidebar,
      & .app-layout-mini,
      & .app-layout-hor,
      & .app-layout-hor-header-fixed {
        min-height: 100%;
      }

      & .mini-sidebar-toggle.ant-layout-sider,
      & .mini-sidebar.ant-layout-sider,
      & .userMiniHeader-sidebar.ant-layout-sider {
        position: absolute;
      }

      & .app-header-mini-sidebar,
      & .app-header-mini,
      & .app-userMiniHeader {
        position: absolute;
        width: 100% !important;
      }

      & .mini-sidebar-toggle.ant-layout-sider-collapsed + .app-layout-mini-sidebar-main .app-header-mini-sidebar,
      & .mini-sidebar.ant-layout-sider-collapsed + .app-layout-mini-main .app-header-mini,
      & .userMiniHeader-sidebar.ant-layout-sider-collapsed + .app-layout-userMiniHeader-main .app-userMiniHeader {
        width: 100% !important;
      }

      & .app-layout-mini-sidebar,
      & .app-layout-mini,
      & .app-layout-userMiniHeader {
        padding-top: 0;
      }

      & .app-layout-mini-sidebar-main,
      & .app-layout-mini-main,
      & .app-layout-userMiniHeader-main {
        padding-top: 71px;
      }
    }
  }

  color-active,
  color-true {
    color: #22bb00;
  }
  color-blocked,
  color-false {
    color: #ff6276;
  }
  color-on hold {
    color: #f9b600;
  }
  color-lost {
    color: #ff6276;
  }
  color-pending {
    color: #ae4600;
  }
  color-inactive {
    color: #ff6276;
  }

  /* Dashboard clickable cards — green gradient hover like Analytics */
  .dashboard-hover-card {
    transition: all 0.3s ease;
    cursor: pointer;

    &:hover {
      background: linear-gradient(278.36deg, #00CA98 47.11%, #079A76 97.82%) !important;
      border-color: transparent !important;

      & span, & div, & p {
        color: #fff !important;
      }
      & svg {
        color: #fff !important;
      }
      & img {
        filter: brightness(0) invert(1) !important;
      }
      /* Preserve icon wrappers with colored backgrounds — don't change their bg or icon color */
      & [class*="bg-warning"], & [class*="bg-red-"], & [class*="bg-orange-"],
      & [class*="bg-violet-"] {
        & svg {
          color: inherit !important;
        }
      }
    }
  }
`;

const SCROLLBAR_CSS = `
*::-webkit-scrollbar { width: 8px !important; height: 8px !important; }
*::-webkit-scrollbar-track { background: transparent !important; }
*::-webkit-scrollbar-thumb { background: #00795b !important; border-radius: 99px !important; }
*::-webkit-scrollbar-thumb:hover { background: #33d5ad !important; }
*::-webkit-scrollbar-corner { background: transparent !important; }
*::-webkit-scrollbar-button { display: none !important; width: 0 !important; height: 0 !important; }

/* Virtual table custom <div> scrollbar — always visible, match native */
[class*="ant-table-tbody-virtual-scrollbar"][class*="vertical"] { visibility: visible !important; }
[class*="ant-table-tbody-virtual-scrollbar"][class*="horizontal"] { visibility: visible !important; }
[class*="ant-table-tbody-virtual-scrollbar-thumb"] { background: #00795b !important; border-radius: 99px !important; }
[class*="ant-table-tbody-virtual-scrollbar-thumb"]:hover { background: #33d5ad !important; }
`;

/** Mount once at app root — injects custom scrollbar CSS globally. */
export function useScrollbarGlow() {
  useEffect(() => {
    const style = document.createElement('style');
    style.id = 'nxsys-scrollbar';
    style.textContent = SCROLLBAR_CSS;
    document.head.insertBefore(style, document.head.firstChild);

    return () => {
      document.getElementById('nxsys-scrollbar')?.remove();
    };
  }, []);
}
